import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Wallet, ArrowLeftRight, ShieldCheck, TrendingUp, Coins, Bell, ArrowLeft, ArrowDownToLine,
} from 'lucide-react'
import { PanelShell } from '../components/Layout'
import { Card, Rows, Empty, statusBadge } from '../components/UI'
import { CoinIcon, Sparkline } from '../components/Market'
import { useAuth, useTickers } from '../store'
import { get } from '../lib/api'
import { clsx, num, fa, pct, changeClass, faDate, compact, asArray} from '../lib/format'
import { LeveragePositions, useLeverageConfig } from '../components/Leverage'

export function Donut({ items }: { items: { share: number; color: string; faName: string }[] }) {
  const R = 54, C = 2 * Math.PI * R
  let acc = 0
  return (
    <div className="flex items-center gap-6">
      <svg width="140" height="140" viewBox="0 0 140 140" className="-rotate-90">
        <circle cx="70" cy="70" r={R} fill="none" stroke="rgba(255,255,255,.05)" strokeWidth="16" />
        {items.slice(0, 7).map((it, i) => {
          const len = (it.share / 100) * C
          const el = (
            <circle key={i} cx="70" cy="70" r={R} fill="none" stroke={it.color} strokeWidth="16"
              strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-acc} strokeLinecap="butt" opacity={0.9} />
          )
          acc += len
          return el
        })}
      </svg>
      <div className="flex-1 space-y-2">
        {items.slice(0, 6).map((it) => (
          <div key={it.faName} className="flex items-center gap-2 text-xs">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: it.color }} />
            <span className="text-slate-300">{it.faName}</span>
            <span className="num mr-auto text-slate-500">{num(it.share, 1)}٪</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/** موقعیت‌های اهرمی روی داشبورد — وقتی خاموش است چیزی رندر نمی‌شود */
function LevBlock() {
  const { cfg } = useLeverageConfig()
  // نه `cfg.enabled`: اگر اهرم خاموش شده ولی کاربر موقعیت باز دارد، باید
  // همچنان ببیندش و بتواند ببنددش. تصمیم پنهان‌سازی داخل خودِ کامپوننت است.
  if (!cfg) return null
  return <div className="mt-5"><LeveragePositions cfg={cfg} /></div>
}

export default function Dashboard() {
  const { user } = useAuth()
  const irtRows = useTickers('IRT')
  const [portfolio, setPortfolio] = useState<any>(null)
  const [orders, setOrders] = useState<any[] | null>(null)
  const [txs, setTxs] = useState<any[] | null>(null)

  useEffect(() => {
    get('/wallet/portfolio').then(setPortfolio).catch(() => {})
    get('/orders?limit=6').then((r: any) => setOrders(asArray(r))).catch(() => setOrders([]))
    get('/wallet/transactions?limit=6').then((r: any) => setTxs(asArray(r))).catch(() => setTxs([]))
  }, [])

  const kycPercent = ((user?.kycLevel ?? 0) / 3) * 100
  const top = irtRows.slice(0, 5)

  const actions = [
    { to: '/wallet', label: 'واریز تومان', icon: ArrowDownToLine, color: '#00E39A' },
    { to: '/convert', label: 'خرید سریع', icon: ArrowLeftRight, color: '#7C5CFF' },
    { to: '/trade/BTCIRT', label: 'معامله حرفه‌ای', icon: TrendingUp, color: '#22D3EE' },
    { to: '/staking', label: 'استیکینگ', icon: Coins, color: '#FFB020' },
  ]

  return (
    <PanelShell title={`سلام ${user?.fullName || 'کاربر ارزینو'} 👋`} subtitle="خلاصه وضعیت حساب و دارایی‌های شما">
      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="relative overflow-hidden lg:col-span-2">
          <div className="pointer-events-none absolute -left-16 -top-20 h-56 w-56 rounded-full bg-brand-500/20 blur-[80px]" />
          <div className="relative">
            <div className="mb-1 text-sm text-slate-400">ارزش کل دارایی‌ها</div>
            <div className="num mb-1 text-3xl font-black text-white sm:text-4xl">
              {num(portfolio?.total ?? user?.totalIrt ?? 0)} <span className="text-lg font-semibold text-slate-400">تومان</span>
            </div>
            <div className="num text-xs text-slate-500">≈ {compact(portfolio?.total ?? 0)} تومان</div>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/wallet" className="btn-primary text-xs">مدیریت کیف پول <ArrowLeft className="h-3.5 w-3.5" /></Link>
              <Link to="/history" className="btn-ghost text-xs">تاریخچه</Link>
            </div>
          </div>
        </Card>

        <Card>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-bold text-white">احراز هویت</h3>
            <span className="chip text-[11px] text-brand-200">سطح {fa(user?.kycLevel ?? 0)}</span>
          </div>
          <div className="mb-3 h-2 overflow-hidden rounded-full bg-white/[.06]">
            <div className="h-full rounded-full bg-gradient-to-l from-brand-600 to-aqua transition-all duration-700"
              style={{ width: `${kycPercent}%` }} />
          </div>
          <p className="mb-4 text-xs leading-6 text-slate-400">
            {user?.kycLevel === 0 ? 'برای فعال‌سازی معاملات، احراز هویت سطح ۱ را کامل کنید.'
              : user?.kycLevel === 1 ? 'برای برداشت ریالی و رمزارزی، سطح ۲ را تکمیل کنید.'
              : user?.kycLevel === 2 ? 'با ارتقا به سطح ۳ سقف برداشت شما ۴ برابر می‌شود.'
              : 'حساب شما در بالاترین سطح احراز هویت قرار دارد.'}
          </p>
          <Link to="/kyc" className="btn-ghost w-full text-xs">
            <ShieldCheck className="h-4 w-4" /> {user?.kycLevel === 3 ? 'مشاهده وضعیت' : 'ارتقای سطح'}
          </Link>
        </Card>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {actions.map((a) => (
          <Link key={a.to} to={a.to}>
            <Card hover className="flex items-center gap-3 !py-4">
              <span className="grid h-11 w-11 place-items-center rounded-xl"
                style={{ background: `${a.color}1A`, border: `1px solid ${a.color}44`, color: a.color }}>
                <a.icon className="h-5 w-5" />
              </span>
              <span className="text-sm font-semibold text-white">{a.label}</span>
              <ArrowLeft className="mr-auto h-4 w-4 text-slate-600" />
            </Card>
          </Link>
        ))}
      </div>

      <LevBlock />

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Card>
          <h3 className="mb-5 font-bold text-white">ترکیب دارایی‌ها</h3>
          {!portfolio ? <Rows n={3} /> : asArray<any>(portfolio, 'items').length === 0
            ? <Empty text="هنوز دارایی ندارید" icon={<Wallet className="h-6 w-6" />} />
            : <Donut items={asArray<any>(portfolio, 'items')} />}
        </Card>

        <Card>
          <h3 className="mb-4 font-bold text-white">بازارهای برتر</h3>
          <div className="space-y-1">
            {top.map((t) => (
              <Link key={t.symbol} to={`/trade/${t.symbol}`}
                className="flex items-center gap-3 rounded-xl p-2 transition hover:bg-white/[.05]">
                <CoinIcon symbol={t.base} color={t.color} size={30} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-white">{t.faName}</div>
                  <div className="num text-[11px] text-slate-500">{num(t.last)}</div>
                </div>
                <Sparkline data={t.sparkline} up={t.change >= 0} w={70} h={26} />
                <span className={clsx('num w-14 text-left text-xs font-semibold', changeClass(t.change))}>{pct(t.change)}</span>
              </Link>
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Card className="!p-0">
          <div className="flex items-center justify-between border-b border-white/[.06] p-4">
            <h3 className="font-bold text-white">آخرین سفارش‌ها</h3>
            <Link to="/history" className="text-xs text-brand-300 hover:underline">همه</Link>
          </div>
          <div className="p-2">
            {orders === null ? <div className="p-3"><Rows n={3} /></div>
              : orders.length === 0 ? <Empty text="سفارشی ثبت نشده است" />
              : orders.map((o) => (
                <div key={o.id} className="flex items-center gap-3 border-b border-white/[.04] px-3 py-2.5 last:border-0">
                  <span className={clsx('text-xs font-bold', o.side === 'buy' ? 'text-up' : 'text-down')}>
                    {o.side === 'buy' ? 'خرید' : 'فروش'}
                  </span>
                  <span className="num text-xs text-slate-300" dir="ltr">{o.symbol}</span>
                  <span className="num text-xs text-slate-500">{num(o.amount, 4)}</span>
                  <span className="mr-auto">{statusBadge(o.status)}</span>
                </div>
              ))}
          </div>
        </Card>

        <Card className="!p-0">
          <div className="flex items-center justify-between border-b border-white/[.06] p-4">
            <h3 className="font-bold text-white">آخرین تراکنش‌ها</h3>
            <Link to="/history" className="text-xs text-brand-300 hover:underline">همه</Link>
          </div>
          <div className="p-2">
            {txs === null ? <div className="p-3"><Rows n={3} /></div>
              : txs.length === 0 ? <Empty text="تراکنشی ثبت نشده است" />
              : txs.map((t) => (
                <div key={t.id} className="flex items-center gap-3 border-b border-white/[.04] px-3 py-2.5 last:border-0">
                  <span className={clsx('text-xs font-bold', t.kind === 'withdraw' ? 'text-down' : 'text-up')}>
                    {t.kind === 'deposit' ? 'واریز' : t.kind === 'withdraw' ? 'برداشت' : t.kind === 'fee' ? 'کارمزد' : 'پاداش'}
                  </span>
                  <span className="num text-xs text-slate-300">{num(t.amount, 4)} {t.currency}</span>
                  <span className="mr-auto text-[11px] text-slate-500">{faDate(t.createdAt, false)}</span>
                </div>
              ))}
          </div>
        </Card>
      </div>
    </PanelShell>
  )
}
